/**
 * Quick Check feature page
 *
 * Allows users to preview Media Magic's impact on their own websites.
 */

import React, { useMemo, useState } from 'react';
import { useSeo } from '../hooks/useSeo';
import { Info, Monitor, ScanSearch, Smartphone } from 'lucide-react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import {
  demoApi,
  DemoMetrics,
  DemoRecord,
  DemoStatusResponse,
  ViewportInfo,
  ViewportStartDemo,
} from '../services/demoApi';
import './TryNow.css';

interface TryNowProps {
  userId?: string;
}

type DemoViewStatus = 'idle' | 'loading' | 'ready' | 'failed';

interface ViewportDemoEntry extends DemoStatusResponse {
  viewportKey: string;
  viewportLabel: string;
  viewport: ViewportInfo;
}

interface DemoGroupState {
  crawlGroupId?: string;
  url?: string;
  status: DemoViewStatus;
  error?: string;
  viewportDemos: Record<string, ViewportDemoEntry>;
  pollingCount: number;
  isSharedView?: boolean;
}

interface HistoryGroup {
  crawlGroupId: string;
  url: string;
  createdAt: string;
  demos: Record<string, DemoRecord>;
}

interface TabState {
  historyItems: HistoryGroup[];
  historyLoading: boolean;
  historyError?: string;
  historyFetched: boolean;
  nextToken?: string;
  hasMore: boolean;
}

const VIEWPORT_ORDER = ['mobile', 'desktop_1080p', 'desktop_1080p_hdpi', 'desktop_2k', 'desktop_4k'];
const VIEWPORT_LABELS: Record<string, string> = {
  mobile: 'Mobile',
  desktop_1080p: '1080p',
  desktop_1080p_hdpi: '1080p Retina',
  desktop_2k: '2K',
  desktop_4k: '4K',
};
const VIEWPORT_DEFAULTS: Record<string, ViewportInfo> = {
  mobile: { width: 390, height: 844, deviceScaleFactor: 3 },
  desktop_1080p: { width: 1920, height: 1080, deviceScaleFactor: 1 },
  desktop_1080p_hdpi: { width: 1920, height: 1080, deviceScaleFactor: 2 },
  desktop_2k: { width: 2560, height: 1440, deviceScaleFactor: 1 },
  desktop_4k: { width: 3840, height: 2160, deviceScaleFactor: 1 },
};
const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 150;
const BACKEND_STAGES = [
  {
    title: 'Spinning up your preview',
    detail: 'Getting everything ready behind the scenes.',
  },
  {
    title: 'Taking in your site',
    detail: 'Loading the page the way a real visitor would see it.',
  },
  {
    title: 'Finding the visual story',
    detail: 'Picking out the images that shape the experience.',
  },
  {
    title: 'Giving each image a better fit',
    detail: 'Resizing and reframing visuals for every screen.',
  },
  {
    title: 'Putting it all together',
    detail: 'Wrapping up your polished previews so they are ready to explore.',
  },
];

const TryNowDisclaimer: React.FC = () => (
  <div className="try-now-disclaimer" role="status">
    <div className="try-now-disclaimer__icon" aria-hidden="true">
      <Info size={17} strokeWidth={2} />
    </div>
    <div>
      <strong>This is a preview — not how you'll use Media Magic.</strong>
      <p>
        We optimized a snapshot of your page to show what's possible. In production, Media Magic runs
        automatically for every real visitor — no iframes, no manual steps.{' '}
        <Link to="/docs">Get started</Link> to make these gains permanent.
      </p>
    </div>
  </div>
);

function groupHistoryDemos(items: DemoRecord[]): HistoryGroup[] {
  const groups = new Map<string, HistoryGroup>();

  items.forEach((demo) => {
    const groupId = demo.crawlGroupId || demo.demoId;
    const existing = groups.get(groupId);
    if (existing) {
      existing.demos[demo.viewportKey || demo.demoId] = demo;
      if (demo.createdAt > existing.createdAt) {
        existing.createdAt = demo.createdAt;
      }
      return;
    }

    groups.set(groupId, {
      crawlGroupId: groupId,
      url: demo.url,
      createdAt: demo.createdAt,
      demos: {
        [demo.viewportKey || demo.demoId]: demo,
      },
    });
  });

  return Array.from(groups.values()).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

function deriveGroupStatus(viewportDemos: Record<string, ViewportDemoEntry>): DemoViewStatus {
  const demos = Object.values(viewportDemos);
  if (demos.length === 0) {
    return 'idle';
  }
  if (demos.some((demo) => demo.status === 'COMPLETED')) {
    return demos.every((demo) => demo.status === 'COMPLETED' || demo.status === 'FAILED')
      ? 'ready'
      : 'loading';
  }
  if (demos.every((demo) => demo.status === 'FAILED')) {
    return 'failed';
  }
  return 'loading';
}

function toViewportEntry(
  item: Pick<DemoStatusResponse, 'demoId' | 'status' | 'viewportKey' | 'viewportLabel' | 'viewport' | 'metrics' | 'imageCount' | 'demoUrl' | 'errorMessage' | 'crawlGroupId' | 'url'>
): ViewportDemoEntry | null {
  const viewportKey = item.viewportKey || 'desktop_1080p';
  const viewportLabel = item.viewportLabel || VIEWPORT_LABELS[viewportKey] || '1080p';
  const viewport = {
    ...(VIEWPORT_DEFAULTS[viewportKey] || VIEWPORT_DEFAULTS.desktop_1080p),
    ...(item.viewport || {}),
  };

  return {
    demoId: item.demoId,
    crawlGroupId: item.crawlGroupId,
    url: item.url,
    viewportKey,
    viewportLabel,
    viewport,
    status: item.status,
    metrics: item.metrics,
    imageCount: item.imageCount,
    demoUrl: item.demoUrl,
    errorMessage: item.errorMessage,
  };
}

function formatViewportDimensions(viewport?: ViewportInfo): string {
  if (!viewport) return 'Unknown size';
  const dpr = viewport.deviceScaleFactor || 1;
  if (dpr > 1) {
    return `${viewport.width * dpr} × ${viewport.height * dpr}`;
  }
  return `${viewport.width} × ${viewport.height}`;
}

function getDisplayUrlParts(rawUrl?: string): { hostname: string; fullUrl: string; path: string } | null {
  if (!rawUrl) return null;

  try {
    const parsed = new URL(rawUrl);
    return {
      hostname: parsed.hostname,
      fullUrl: parsed.toString(),
      path: `${parsed.pathname}${parsed.search}` || '/',
    };
  } catch {
    return {
      hostname: rawUrl,
      fullUrl: rawUrl,
      path: '',
    };
  }
}

function getFaviconUrl(rawUrl?: string): string | null {
  if (!rawUrl) return null;

  try {
    const parsed = new URL(rawUrl);
    return `https://www.google.com/s2/favicons?domain=${parsed.hostname}&sz=64`;
  } catch {
    return null;
  }
}

function getViewportForDisplay(demo?: ViewportDemoEntry): ViewportInfo | undefined {
  if (!demo) {
    return undefined;
  }

  const metricViewport = demo.metrics?.viewport;
  if (!metricViewport) {
    return {
      ...(VIEWPORT_DEFAULTS[demo.viewportKey] || VIEWPORT_DEFAULTS.desktop_1080p),
      ...demo.viewport,
    };
  }

  return {
    width: metricViewport.width,
    height: metricViewport.height,
    deviceScaleFactor:
      metricViewport.deviceScaleFactor ??
      demo.viewport?.deviceScaleFactor ??
      VIEWPORT_DEFAULTS[demo.viewportKey]?.deviceScaleFactor,
  };
}

function getBackendStageIndex(
  data: DemoGroupState,
  totalViewports: number,
  completeCount: number,
  failedCount: number
): number {
  if (completeCount + failedCount >= totalViewports && totalViewports > 0) {
    return BACKEND_STAGES.length - 1;
  }
  if (completeCount > 0) {
    return 4;
  }
  if (data.pollingCount >= 8) {
    return 3;
  }
  if (data.pollingCount >= 4) {
    return 2;
  }
  if (data.pollingCount >= 1) {
    return 1;
  }
  return 0;
}

export const TryNow: React.FC<TryNowProps> = ({ userId }) => {
  useSeo('Try Media Magic — See It on Your Site', 'Paste your site URL and see how Media Magic would optimize your images in real time — no signup required.');
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [urlInput, setUrlInput] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [demoState, setDemoState] = useState<DemoGroupState>({
    status: 'idle',
    viewportDemos: {},
    pollingCount: 0,
  });
  const [tabState, setTabState] = useState<TabState>({
    historyItems: [],
    historyLoading: false,
    historyFetched: false,
    hasMore: false,
  });
  const shareGroupId = searchParams.get('group');
  const isSharedPreview = location.pathname === '/preview-results';
  const isDedicatedResultsPage = location.pathname === '/try-now/results';
  const isResultMode = isSharedPreview || isDedicatedResultsPage;

  React.useEffect(() => {
    if (!shareGroupId) {
      return;
    }

    let cancelled = false;

    setTabState((prev) => ({
      ...prev,
    }));
    setDemoState((prev) => ({
      ...prev,
      status: 'loading',
      error: undefined,
      viewportDemos: {},
      pollingCount: 0,
      isSharedView: true,
    }));

    void demoApi
      .getSharedResult(shareGroupId)
      .then((result) => {
        if (cancelled) {
          return;
        }

        const viewportDemos = result.demos.reduce<Record<string, ViewportDemoEntry>>((acc, demo) => {
          const entry = toViewportEntry({
            ...demo,
            crawlGroupId: result.crawlGroupId,
            url: result.url,
          });
          if (entry) {
            acc[entry.viewportKey] = entry;
          }
          return acc;
        }, {});

        setDemoState({
          crawlGroupId: result.crawlGroupId,
          url: result.url,
          status: deriveGroupStatus(viewportDemos),
          viewportDemos,
          pollingCount: 0,
          isSharedView: true,
        });
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        setDemoState({
          status: 'failed',
          error: error instanceof Error ? error.message : 'Failed to load shared result',
          viewportDemos: {},
          pollingCount: 0,
          isSharedView: true,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [shareGroupId]);

  React.useEffect(() => {
    if (!shareGroupId || demoState.status !== 'loading') {
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      void demoApi
        .getSharedResult(shareGroupId)
        .then((result) => {
          if (cancelled) {
            return;
          }

          const viewportDemos = result.demos.reduce<Record<string, ViewportDemoEntry>>((acc, demo) => {
            const entry = toViewportEntry({
              ...demo,
              crawlGroupId: result.crawlGroupId,
              url: result.url,
            });
            if (entry) {
              acc[entry.viewportKey] = entry;
            }
            return acc;
          }, {});

          setDemoState({
            crawlGroupId: result.crawlGroupId,
            url: result.url,
            status: deriveGroupStatus(viewportDemos),
            viewportDemos,
            pollingCount: demoState.pollingCount + 1,
            isSharedView: true,
          });
        })
        .catch(() => {
          if (!cancelled) {
            window.setTimeout(() => {
              setDemoState((prev) => ({ ...prev, pollingCount: prev.pollingCount + 1 }));
            }, 0);
          }
        });
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [demoState.pollingCount, demoState.status, shareGroupId]);

  const pollViewportDemo = async (
    demo: ViewportStartDemo,
    crawlGroupId: string,
    url: string,
    pollingCount: number
  ) => {
    if (!userId) {
      return;
    }

    if (pollingCount >= MAX_POLLS) {
      setDemoState((prev) => ({
        ...prev,
        status: deriveGroupStatus(prev.viewportDemos),
        error: 'Demo processing timed out. Some viewports may still be pending.',
      }));
      return;
    }

    try {
      const data = await demoApi.getDemoStatus(userId, demo.demoId);
      const entry = toViewportEntry({
        ...data,
        crawlGroupId,
        url,
        viewportKey: data.viewportKey || demo.viewportKey,
        viewportLabel: data.viewportLabel || demo.viewportLabel,
        viewport: data.viewport || demo.viewport,
      });

      if (!entry) {
        return;
      }

      setDemoState((prev) => {
        const viewportDemos = {
          ...prev.viewportDemos,
          [entry.viewportKey]: entry,
        };
        return {
          ...prev,
          crawlGroupId,
          url,
          viewportDemos,
          pollingCount: Math.max(prev.pollingCount, pollingCount + 1),
          status: deriveGroupStatus(viewportDemos),
          error: prev.error,
        };
      });

      if (data.status === 'PENDING' || data.status === 'PROCESSING') {
        setTimeout(
          () => pollViewportDemo(demo, crawlGroupId, url, pollingCount + 1),
          POLL_INTERVAL_MS
        );
      }
    } catch (error) {
      setDemoState((prev) => {
        const failedEntry = toViewportEntry({
          demoId: demo.demoId,
          crawlGroupId,
          url,
          viewportKey: demo.viewportKey,
          viewportLabel: demo.viewportLabel,
          viewport: demo.viewport,
          status: 'FAILED',
          imageCount: 0,
          errorMessage: error instanceof Error ? error.message : 'Failed to get demo status',
        });

        const viewportDemos = failedEntry
          ? {
              ...prev.viewportDemos,
              [failedEntry.viewportKey]: failedEntry,
            }
          : prev.viewportDemos;

        return {
          ...prev,
          crawlGroupId,
          url,
          viewportDemos,
          status: deriveGroupStatus(viewportDemos),
          error: error instanceof Error ? error.message : 'Failed to get demo status',
        };
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!urlInput.trim()) {
      setDemoState({ status: 'failed', error: 'Please enter a URL', viewportDemos: {}, pollingCount: 0 });
      return;
    }

    let url = urlInput.trim();
    if (!url.match(/^https?:\/\//)) {
      url = `https://${url}`;
    }

    setDemoState({ status: 'loading', url, viewportDemos: {}, pollingCount: 0 });

    try {
      let result;
      if (userId) {
        // Authenticated user flow
        result = await demoApi.startDemo(userId, url);
      } else {
        // Guest user flow
        if (!turnstileToken) {
          setDemoState({
            status: 'failed',
            error: 'Please complete the CAPTCHA',
            viewportDemos: {},
            pollingCount: 0,
          });
          return;
        }
        result = await demoApi.startDemoGuest(url, turnstileToken);
      }

      const initialDemos = result.demos.reduce<Record<string, ViewportDemoEntry>>((acc, demo) => {
        acc[demo.viewportKey] = {
          demoId: demo.demoId,
          crawlGroupId: result.crawlGroupId,
          url,
          viewportKey: demo.viewportKey,
          viewportLabel: demo.viewportLabel,
          viewport: demo.viewport,
          status: demo.status,
          imageCount: 0,
        };
        return acc;
      }, {});

      setDemoState({
        crawlGroupId: result.crawlGroupId,
        url,
        status: 'loading',
        viewportDemos: initialDemos,
        pollingCount: 0,
      });
      navigate(`/try-now/results?group=${encodeURIComponent(result.crawlGroupId)}`);

      // Only poll if logged in (guest users use shared results)
      if (userId) {
        result.demos.forEach((demo) => {
          void pollViewportDemo(demo, result.crawlGroupId, url, 0);
        });
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to start demo';
      setDemoState({
        status: 'failed',
        error: errorMsg,
        viewportDemos: {},
        pollingCount: 0,
      });
      // Reset Turnstile token on error so user can retry
      if (!userId) {
        setTurnstileToken(null);
      }
    }
  };

  const handleReset = () => {
    setUrlInput('');
    setSearchParams({});
    setDemoState({ status: 'idle', viewportDemos: {}, pollingCount: 0 });
    navigate('/try-now');
  };

  const fetchHistory = async (nextToken?: string) => {
    if (!userId) {
      setTabState((prev) => ({
        ...prev,
        historyItems: [],
        historyLoading: false,
        historyError: 'Sign in to view your history',
        historyFetched: true,
        hasMore: false,
      }));
      return;
    }

    setTabState((prev) => ({ ...prev, historyLoading: true, historyError: undefined }));

    try {
      const result = await demoApi.listDemos(userId, 10, nextToken);
      setTabState((prev) => ({
        ...prev,
        historyItems: nextToken ? [...prev.historyItems, ...groupHistoryDemos(result.demos)] : groupHistoryDemos(result.demos),
        historyLoading: false,
        historyFetched: true,
        nextToken: result.nextToken,
        hasMore: !!result.nextToken,
      }));
    } catch (error) {
      setTabState((prev) => ({
        ...prev,
        historyError: error instanceof Error ? error.message : 'Failed to load history',
        historyLoading: false,
        historyFetched: true,
        hasMore: false,
      }));
    }
  };

  React.useEffect(() => {
    if (isResultMode || tabState.historyFetched || tabState.historyLoading || !userId) {
      return;
    }

    void fetchHistory();
  }, [isResultMode, tabState.historyFetched, tabState.historyLoading, userId]);

  const handleSelectHistoryGroup = (group: HistoryGroup) => {
    navigate(`/try-now/results?group=${encodeURIComponent(group.crawlGroupId)}`);
  };

  return (
    <div className="try-now-container">
      {isResultMode && (
        <div className="try-now-results-section">
          <DemoResults
            data={demoState}
            onReset={handleReset}
            isSharedView={isSharedPreview}
            showBackButton={!isSharedPreview}
          />
        </div>
      )}

      {!isResultMode && (
        <>
          <div className="try-now-header">
          </div>
          <div className="try-now-form-section">
            <form onSubmit={handleSubmit} style={{ maxWidth: 600, margin: '0 auto' }}>
              <div className="try-now-form">
                <div className="form-group">
                  <input
                    id="url-input"
                    type="text"
                    placeholder="https://example.com"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    className="url-input"
                  />
                </div>
                <button
                  type="submit"
                  className="submit-button"
                  disabled={(!userId && !turnstileToken) || demoState.status === 'loading'}
                >
                  {demoState.status === 'loading' ? 'Running…' : 'Run Quick Check'}
                </button>
              </div>
              {!userId && (
                <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
                  <Turnstile
                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                    onSuccess={(token: string) => setTurnstileToken(token)}
                    onExpire={() => setTurnstileToken(null)}
                    onError={() => setTurnstileToken(null)}
                    options={{ theme: 'light' }}
                  />
                </div>
              )}
            </form>

            {demoState.status === 'failed' && demoState.error && (
              <div className="error-message">
                <p>{demoState.error}</p>
              </div>
            )}
          </div>

          <TryNowDisclaimer />

          {userId && (
            <>
              <h3 style={{ marginTop: '2rem', marginBottom: '1rem', fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary)' }}>Previous Runs</h3>

              <HistoryList
                items={tabState.historyItems}
                loading={tabState.historyLoading}
                error={tabState.historyError}
                onSelectGroup={handleSelectHistoryGroup}
                hasMore={tabState.hasMore}
                onLoadMore={() => fetchHistory(tabState.nextToken)}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

interface DemoResultsProps {
  data: DemoGroupState;
  onReset: () => void;
  isSharedView?: boolean;
  showBackButton?: boolean;
}

const DemoResults: React.FC<DemoResultsProps> = ({
  data,
  onReset,
  isSharedView = false,
  showBackButton = false,
}) => {
  const navigate = useNavigate();
  const completedViewports = useMemo(
    () =>
      VIEWPORT_ORDER.map((key) => data.viewportDemos[key]).filter(
        (demo): demo is ViewportDemoEntry => Boolean(demo)
      ),
    [data.viewportDemos]
  );

  const firstReadyViewport = completedViewports.find((demo) => demo.status === 'COMPLETED');
  const [activeViewportKey, setActiveViewportKey] = useState<string | undefined>(
    firstReadyViewport?.viewportKey || completedViewports[0]?.viewportKey
  );
  const [copiedShareState, setCopiedShareState] = useState<'idle' | 'done' | 'failed'>('idle');
  React.useEffect(() => {
    if (!activeViewportKey || !data.viewportDemos[activeViewportKey]) {
      setActiveViewportKey(firstReadyViewport?.viewportKey || completedViewports[0]?.viewportKey);
      return;
    }
    if (data.viewportDemos[activeViewportKey]?.status !== 'COMPLETED' && firstReadyViewport) {
      setActiveViewportKey(firstReadyViewport.viewportKey);
    }
  }, [activeViewportKey, completedViewports, data.viewportDemos, firstReadyViewport]);

  const activeDemo = activeViewportKey ? data.viewportDemos[activeViewportKey] : firstReadyViewport;
  const metrics = activeDemo?.metrics;
  const coverage = metrics?.coverage;
  const completeCount = completedViewports.filter((demo) => demo.status === 'COMPLETED').length;
  const failedCount = completedViewports.filter((demo) => demo.status === 'FAILED').length;
  const totalViewports = completedViewports.length;
  const allViewportsFinished = totalViewports > 0 && completeCount + failedCount === totalViewports;
  const urlParts = getDisplayUrlParts(data.url);
  const faviconUrl = getFaviconUrl(data.url);
  const cleanPath = urlParts
    ? urlParts.path === '/'
      ? ''
      : urlParts.path.replace(/\/$/, '').replace(/^\//, '')
    : '';
  const currentStageIndex = getBackendStageIndex(data, totalViewports, completeCount, failedCount);
  const currentStage = BACKEND_STAGES[currentStageIndex];
  const visibleStageCount = Math.min(BACKEND_STAGES.length, Math.max(2, currentStageIndex + 2));
  const visibleStages = BACKEND_STAGES.slice(0, visibleStageCount);
  const hiddenStageCount = BACKEND_STAGES.length - visibleStages.length;
  const progressPercent = ((currentStageIndex + 1) / BACKEND_STAGES.length) * 100;

  const formatBytes = (bytes: number): string => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const canShareResults =
    Boolean(data.crawlGroupId) && data.status === 'ready' && completedViewports.some((demo) => demo.status === 'COMPLETED');

  const shareUrl = React.useMemo(() => {
    if (!data.crawlGroupId) {
      return null;
    }
    const basePath = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
    const pathname = `${basePath}/preview-results`;
    const url = new URL(window.location.href);
    url.pathname = pathname || '/preview-results';
    url.search = '';
    url.searchParams.set('group', data.crawlGroupId);
    return url.toString();
  }, [data.crawlGroupId]);

  const handleSelectViewport = (viewportKey: string) => {
    const currentScrollY = window.scrollY;
    setActiveViewportKey(viewportKey);
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: currentScrollY });
    });
  };

  const handleCopyShareLink = async (url: string | null) => {
    if (!url) {
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      setCopiedShareState('done');
    } catch {
      setCopiedShareState('failed');
    }

    window.setTimeout(() => {
      setCopiedShareState('idle');
    }, 2000);
  };

  return (
    <div className="demo-results">
      <div className="results-header">
        {showBackButton && (
          <button onClick={onReset} className="history-back-button" aria-label="Back to history">
            ‹
          </button>
        )}
        <div className="results-heading">
          <h2>Media Magic Optimized Preview</h2>
          {canShareResults && shareUrl && (
              <button
                type="button"
                onClick={() => handleCopyShareLink(shareUrl)}
                className="share-results-button share-inline-button"
              >
                {copiedShareState === 'done'
                  ? 'Copied'
                  : copiedShareState === 'failed'
                    ? 'Copy failed'
                    : 'Share Results'}
              </button>
            )}
        </div>
        {!showBackButton && (
          <button
            onClick={isSharedView ? () => navigate('/try-now') : onReset}
            className="reset-button"
          >
            {isSharedView ? 'Run Your Own Quick Check' : 'Check Another Site'}
          </button>
        )}
      </div>

      <TryNowDisclaimer />

      {urlParts && (
        <div className="results-url-card">
          {faviconUrl && <img src={faviconUrl} alt="" className="results-url-favicon" />}
          <div className="results-url-content">
            <div className="results-url-label">Your website url</div>
            <div className="results-url-line" title={urlParts.fullUrl}>
              <span className="results-url-host">{urlParts.hostname}</span>
              {cleanPath && (
                <>
                  <span className="results-url-separator">/</span>
                  <span className="results-url-path">{cleanPath}</span>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {!showBackButton && (
        <div className="viewport-progress">
          {completeCount} of {totalViewports} viewport crawls complete
          {failedCount > 0 ? `, ${failedCount} failed` : ''}
          {data.status === 'loading' ? '.' : ''}
        </div>
      )}

      {!allViewportsFinished && totalViewports > 0 && (
        <div className="backend-loader">
          <div className="backend-loader-copy">
            <div className="backend-loader-topline">
              <div className="backend-loader-label">What&apos;s happening now</div>
              <div className="backend-loader-step-count">
                Step {currentStageIndex + 1} of {BACKEND_STAGES.length}
              </div>
            </div>
            <div className="backend-loader-headline">
              <span className="backend-loader-spinner" aria-hidden="true" />
              <h3>{currentStage.title}</h3>
            </div>
            <p>{currentStage.detail}</p>
            <div className="backend-progress-bar" aria-hidden="true">
              <div className="backend-progress-fill" style={{ width: `${progressPercent}%` }} />
            </div>
          </div>
          <div className="backend-stage-track" aria-label="Processing progress">
            {visibleStages.map((stage, index) => {
              const status =
                index < currentStageIndex ? 'done' : index === currentStageIndex ? 'active' : 'upcoming';
              return (
                <div key={stage.title} className={`backend-stage-chip backend-stage-chip-${status}`}>
                  <span className="backend-stage-dot" />
                  <div className="backend-stage-title">{stage.title}</div>
                </div>
              );
            })}
            {hiddenStageCount > 0 && (
              <div className="backend-stage-chip backend-stage-chip-hidden">+{hiddenStageCount} more</div>
            )}
          </div>
        </div>
      )}

      <div className="viewport-results-shell">
        <ViewportTabBar
          demos={completedViewports}
          activeViewportKey={activeViewportKey}
          onSelect={handleSelectViewport}
        />

        <div className="viewport-results-content">
          {activeDemo?.status === 'COMPLETED' && metrics && (
            <>
              <div className="metrics-grid">
                <div className="metric-card">
                  <div className="metric-label">Size Reduction</div>
                  <div className="metric-value">{metrics.totalReductionPercent.toFixed(1)}%</div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Optimized</div>
                  <div className="metric-value">{formatBytes(metrics.totalTransformedBytes)}</div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Original</div>
                  <div className="metric-value">{formatBytes(metrics.totalOriginalBytes)}</div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Images Optimized</div>
                  <div className="metric-value">
                    {coverage ? coverage.htmlGroupsRewritten : activeDemo.imageCount}
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Images Skipped</div>
                  <div className="metric-value">{metrics.skippedImages?.length || 0}</div>
                </div>
              </div>
            </>
          )}

          {activeDemo?.status === 'COMPLETED' && activeDemo.demoUrl && (
            <div className="preview-section">
              <div className="preview-header">
              <h3>Live Preview</h3>
              <div className="preview-controls">
                {data.url && (
                  <a href={data.url} target="_blank" rel="noopener noreferrer" className="preview-copy-button">
                    View Original
                  </a>
                )}
                  <a href={activeDemo.demoUrl} target="_blank" rel="noopener noreferrer" className="preview-link">
                    Open Preview in New Tab
                  </a>
                </div>
              </div>
              <div className={`iframe-container ${activeDemo.viewportKey === 'mobile' ? 'iframe-mobile' : ''}`}>
                <iframe
                  src={activeDemo.demoUrl}
                  className="demo-iframe"
                  title={`Demo preview ${activeDemo.viewportLabel}`}
                  sandbox="allow-scripts"
                />
              </div>
            </div>
          )}

          {activeDemo?.status === 'FAILED' && (
            <div className="error-message">
              <p>{activeDemo.errorMessage || 'Viewport processing failed.'}</p>
            </div>
          )}

          {activeDemo?.status !== 'COMPLETED' && activeDemo?.status !== 'FAILED' && (
            <div className="try-now-loading-section">
              <div className="spinner"></div>
              <h2>Processing {activeDemo?.viewportLabel || 'viewport'}...</h2>
              <p>Results will appear as each viewport finishes.</p>
              {data.pollingCount > 0 && (
                <p className="polling-status">
                  Elapsed: {Math.floor((data.pollingCount * 2) / 60)}m {(data.pollingCount * 2) % 60}s
                </p>
              )}
            </div>
          )}

          {activeDemo?.status === 'COMPLETED' && metrics && (
            <ImagesBreakdown metrics={metrics} />
          )}
        </div>
      </div>
    </div>
  );
};

interface ViewportTabBarProps {
  demos: ViewportDemoEntry[];
  activeViewportKey?: string;
  onSelect: (viewportKey: string) => void;
}

const ViewportTabBar: React.FC<ViewportTabBarProps> = ({ demos, activeViewportKey, onSelect }) => {
  if (demos.length === 0) {
    return null;
  }

  const getViewportIcon = (key: string) => {
    if (key === 'mobile') return <Smartphone size={14} strokeWidth={2.1} />;
    if (key === 'desktop_4k') return <ScanSearch size={14} strokeWidth={2.1} />;
    return <Monitor size={14} strokeWidth={2.1} />;
  };

  return (
    <div className="viewport-tabs">
      {VIEWPORT_ORDER.map((key) => {
        const demo = demos.find((item) => item.viewportKey === key);
        if (!demo) {
          return null;
        }

        const reduction = demo.metrics?.totalReductionPercent;
        const isReady = demo.status === 'COMPLETED';
        const displayViewport = getViewportForDisplay(demo);

        return (
          <button
            key={key}
            className={`viewport-tab-button ${activeViewportKey === key ? 'active' : ''}`}
            onClick={() => isReady && onSelect(key)}
            disabled={!isReady}
          >
            <div className="viewport-tab-topline">
              <span className="viewport-tab-label">
                <span className="viewport-tab-icon" aria-hidden="true">
                  {getViewportIcon(key)}
                </span>
                <span>{VIEWPORT_LABELS[key] || demo.viewportLabel}</span>
              </span>
            </div>
            <div className="viewport-tab-dimensions">
              <span className="viewport-tab-meta-icon" aria-hidden="true">
                <Monitor size={12} strokeWidth={2.1} />
              </span>
              <span>{formatViewportDimensions(displayViewport)}</span>
            </div>
            <div className="viewport-tab-metric">
              <span className="viewport-tab-meta-icon" aria-hidden="true">
                <ScanSearch size={12} strokeWidth={2.1} />
              </span>
              {demo.status === 'COMPLETED' && typeof reduction === 'number'
                ? `${reduction.toFixed(1)}% optimized`
                : demo.status === 'FAILED'
                  ? 'This viewport failed'
                  : 'Waiting for output'}
            </div>
          </button>
        );
      })}
    </div>
  );
};

const ImagesBreakdown: React.FC<{ metrics: DemoMetrics }> = ({ metrics }) => {
  const formatBytes = (bytes: number): string => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const sortedImages = [...metrics.images].sort(
    (left, right) =>
      right.originalSize - right.transformedSize - (left.originalSize - left.transformedSize),
  );
  const skippedImages = metrics.skippedImages || [];

  return (
    <div className="images-breakdown">
      {metrics.images.length > 0 && (
        <>
          <h3>Images ({metrics.images.length})</h3>
          <div className="images-list">
            {sortedImages.slice(0, 10).map((img, idx) => (
              <div key={idx} className="image-item">
                <a
                  href={img.transformedUrl || img.originalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="image-thumbnail"
                >
                  <img src={img.transformedUrl || img.originalUrl} alt="" loading="lazy" />
                </a>
                <div className="image-info">
                  <a
                    href={img.transformedUrl || img.originalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="image-url"
                    title={img.originalUrl}
                  >
                    {img.originalUrl.split('/').pop() || 'image'}
                  </a>
                  <div className="image-stats">
                    {formatBytes(img.originalSize)} → {formatBytes(img.transformedSize)}
                  </div>
                  {img.statusMessage && img.optimizationStatus === 'kept_original' && (
                    <div className="image-status-note">{img.statusMessage}</div>
                  )}
                </div>
                <div className="image-reduction">
                  {img.optimizationStatus === 'kept_original'
                    ? 'Kept original'
                    : `${((1 - img.transformedSize / img.originalSize) * 100).toFixed(0)}% optimized`}
                </div>
              </div>
            ))}
            {metrics.images.length > 10 && (
              <p className="more-images">+{metrics.images.length - 10} more images</p>
            )}
          </div>
        </>
      )}

      {skippedImages.length > 0 && (
        <>
          <h3>Images Skipped ({skippedImages.length})</h3>
          <div className="images-list">
            {skippedImages.slice(0, 10).map((img, idx) => (
              <div key={`${img.originalUrl}-${idx}`} className="image-item image-item-skipped">
                <a
                  href={img.originalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="image-thumbnail"
                >
                  <img src={img.originalUrl} alt="" loading="lazy" />
                </a>
                <div className="image-info">
                  <a
                    href={img.originalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="image-url"
                    title={img.originalUrl}
                  >
                    {img.originalUrl.split('/').pop() || 'image'}
                  </a>
                  <div className="image-stats">
                    {typeof img.originalSize === 'number' && typeof img.transformedSize === 'number'
                      ? `${formatBytes(img.originalSize)} → ${formatBytes(img.transformedSize)}`
                      : 'Original kept or skipped before transform'}
                  </div>
                  <div className="image-status-note">{img.message}</div>
                </div>
                <div className="image-reduction image-reduction-skipped">Skipped</div>
              </div>
            ))}
            {skippedImages.length > 10 && (
              <p className="more-images">+{skippedImages.length - 10} more skipped images</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

interface HistoryListProps {
  items: HistoryGroup[];
  loading: boolean;
  error?: string;
  onSelectGroup: (group: HistoryGroup) => void;
  hasMore?: boolean;
  onLoadMore?: () => void;
}

const HistoryList: React.FC<HistoryListProps> = ({ items, loading, error, onSelectGroup, hasMore, onLoadMore }) => {
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="history-loading">
        <div className="spinner"></div>
        <p>Loading history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="history-error">
        <p>Failed to load history: {error}</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="history-empty">
        <p>You haven&apos;t tried any websites yet</p>
        <p className="history-empty-hint">Run a quick check on a website to get started</p>
      </div>
    );
  }

  return (
    <div className="history-section">
      <div className="history-list">
        {items.map((group) => {
          const demos = Object.values(group.demos);
          const faviconUrl = getFaviconUrl(group.url);
          const urlParts = getDisplayUrlParts(group.url);
          const cleanPath = urlParts
            ? urlParts.path === '/'
              ? ''
              : urlParts.path.replace(/\/$/, '').replace(/^\//, '')
            : '';
          const bestReduction = demos.reduce((max, demo) => {
            const reduction = demo.metrics?.totalReductionPercent || 0;
            return reduction > max ? reduction : max;
          }, 0);

          return (
            <div
              key={group.crawlGroupId}
              className="history-item"
              onClick={() => onSelectGroup(group)}
              style={{ cursor: 'pointer' }}
            >
              <div className="history-item-main">
                <div className="history-item-heading">
                  {faviconUrl && <img src={faviconUrl} alt="" className="history-item-favicon" />}
                  <div className="history-item-url" title={group.url}>
                    {urlParts ? (
                      <>
                        <span className="history-item-url-host">{urlParts.hostname}</span>
                        {cleanPath && (
                          <>
                            <span className="history-item-url-sep">/</span>
                            <span className="history-item-url-path">{cleanPath}</span>
                          </>
                        )}
                      </>
                    ) : (
                      group.url
                    )}
                  </div>
                </div>
                <div className="history-item-meta">
                  <span className="history-item-date">{formatDate(group.createdAt)}</span>
                  {bestReduction > 0 && (
                    <span className="history-item-reduction">{bestReduction.toFixed(1)}% optimized</span>
                  )}
                </div>
              </div>
              <div className="history-item-arrow">→</div>
            </div>
          );
        })}
      </div>
      {hasMore && onLoadMore && (
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <button
            onClick={onLoadMore}
            disabled={loading}
            style={{
              padding: '0.5rem 1.5rem',
              backgroundColor: 'var(--primary-600)',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            {loading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
};
